import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Chip,
  Grid,
  ToggleButton,
  ToggleButtonGroup,
  useTheme
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { createChart } from 'lightweight-charts';
import { usePrivacyMask } from '../contexts/PrivacyMaskContext';
import { apiRequest } from '../utils/api';

const RANGES = [
  { key: '1d', label: '1D', days: 1 },
  { key: '5d', label: '5D', days: 5 },
  { key: '1mo', label: '1M', days: 30 },
  { key: '3mo', label: '3M', days: 90 },
  { key: '6mo', label: '6M', days: 182 },
  { key: 'ytd', label: 'YTD', days: null }, // computed
  { key: '1y', label: '1Y', days: 365 },
];

const INTERVALS = [
  { key: '5m', label: '5m' },
  { key: '15m', label: '15m' },
  { key: '30m', label: '30m' },
  { key: '1h', label: '1h' },
  { key: '1d', label: '1D' },
  { key: '1wk', label: '1W' },
];

// Yahoo limits intraday intervals to short ranges.
const intervalsForDays = (days) => {
  if (days <= 10) return ['5m', '15m', '30m', '1h', '1d'];
  if (days <= 60) return ['15m', '30m', '1h', '1d'];
  if (days <= 730) return ['1h', '1d', '1wk'];
  return ['1d', '1wk'];
};

const daysForRange = (key) => {
  if (key === 'ytd') {
    const now = new Date();
    return Math.max(1, Math.ceil((now - new Date(now.getFullYear(), 0, 1)) / 86400000));
  }
  return (RANGES.find(r => r.key === key) || {}).days || 30;
};

const isIntradayInterval = (interval) => !['1d', '1wk', '1mo'].includes(interval);

const ChartPopup = ({ open, onClose, symbol }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const containerRef = useRef(null);
  const chartRef = useRef(null);

  const [range, setRange] = useState('1mo');
  const [interval, setInterval] = useState('1d');
  const [candles, setCandles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { privacyMask } = usePrivacyMask();

  const fetchHistoricalData = useCallback(async () => {
    if (!symbol) return;
    setLoading(true);
    setError(null);
    try {
      const response = await apiRequest(
        `/api/stocks/${encodeURIComponent(symbol)}/history?range=${range}&interval=${interval}`
      );
      if (response.data && response.data.candles && response.data.candles.length) {
        setCandles(response.data.candles);
      } else {
        setCandles([]);
        setError('No historical data available for this symbol');
      }
    } catch (err) {
      setCandles([]);
      setError('Failed to load historical data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [symbol, range, interval]);

  useEffect(() => {
    if (open && symbol) {
      fetchHistoricalData();
    }
  }, [open, symbol, fetchHistoricalData]);

  // Build the lightweight-charts candlestick (line fallback when OHLC missing)
  useEffect(() => {
    const el = containerRef.current;
    if (!el || candles.length === 0) return undefined;

    if (chartRef.current) {
      chartRef.current.remove();
      chartRef.current = null;
    }

    const chart = createChart(el, {
      height: 400,
      width: el.clientWidth || 600,
      layout: {
        background: { type: 'solid', color: 'transparent' },
        textColor: theme.palette.text.secondary,
      },
      grid: {
        vertLines: { color: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)' },
        horzLines: { color: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)' },
      },
      rightPriceScale: { borderColor: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)' },
      timeScale: {
        borderColor: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.15)',
        timeVisible: isIntradayInterval(interval),
        secondsVisible: false,
      },
      crosshair: { mode: 0 },
    });
    chartRef.current = chart;

    const intraday = isIntradayInterval(interval);
    // Normalize `time`: unix ts for intraday, 'yyyy-mm-dd' for daily. Drop
    // rows without a usable time — setData throws on undefined/mixed times.
    const timeOf = (c) => {
      if (intraday && c.time != null) return c.time;
      const d = typeof c.date === 'string' ? c.date.slice(0, 10) : null;
      return d && /^\d{4}-\d{2}-\d{2}$/.test(d) ? d : null;
    };

    // Sort ascending + dedupe on time (lightweight-charts requires it)
    const seen = new Set();
    const sorted = candles
      .filter(c => timeOf(c) != null && c.close != null)
      .sort((a, b) => (timeOf(a) < timeOf(b) ? -1 : 1))
      .filter(c => { const t = timeOf(c); if (seen.has(t)) return false; seen.add(t); return true; });
    if (!sorted.length) return undefined;

    // Candle mode only when every kept row has complete OHLC — null members
    // make setData throw.
    const hasOhlc = sorted.every(c => c.open != null && c.high != null && c.low != null);

    try {
      let series;
      if (hasOhlc) {
        series = chart.addCandlestickSeries({
          upColor: '#2e7d32', downColor: '#c62828',
          borderUpColor: '#2e7d32', borderDownColor: '#c62828',
          wickUpColor: '#2e7d32', wickDownColor: '#c62828',
        });
        series.setData(sorted.map(c => ({
          time: timeOf(c), open: c.open, high: c.high, low: c.low, close: c.close,
        })));
      } else {
        series = chart.addLineSeries({ color: '#1976d2', lineWidth: 2 });
        series.setData(sorted.map(c => ({ time: timeOf(c), value: c.close })));
      }
      chart.timeScale().fitContent();
    } catch (e) {
      // A setData failure must not take the whole page down — surface it.
      console.error('ChartPopup chart error:', e);
      chart.remove();
      chartRef.current = null;
      setError('Unable to render chart for this symbol/interval');
      return undefined;
    }

    const onResize = () => chart.applyOptions({ width: el.clientWidth });
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      try { chart.remove(); } catch { /* already removed */ }
      chartRef.current = null;
    };
  }, [candles, interval, theme, isDark]);

  // Cleanup chart when dialog closes
  useEffect(() => {
    if (!open && chartRef.current) {
      chartRef.current.remove();
      chartRef.current = null;
    }
  }, [open]);

  const handleRangeChange = (_, v) => {
    if (!v) return;
    setRange(v);
    const allowed = intervalsForDays(daysForRange(v));
    if (!allowed.includes(interval)) {
      setInterval(allowed[allowed.length - 1]);
    }
  };

  const formatCurrency = (value) => {
    if (privacyMask) return '••••••';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
  };

  const allowedIntervals = intervalsForDays(daysForRange(range));
  const closes = candles.map(c => c.close);
  const last = closes.length ? closes[closes.length - 1] : 0;
  const first = closes.length ? closes[0] : 0;
  const gainLoss = last - first;
  const gainLossPct = first > 0 ? (gainLoss / first) * 100 : 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { height: '80vh', maxHeight: '800px' } }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6">{symbol} Chart</Typography>
          <Chip label={`${INTERVALS.find(i => i.key === interval)?.label} candles`} size="small" variant="outlined" />
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 0 }}>
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <ToggleButtonGroup size="small" exclusive value={range} onChange={handleRangeChange}>
            {RANGES.map(r => <ToggleButton key={r.key} value={r.key}>{r.label}</ToggleButton>)}
          </ToggleButtonGroup>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={interval}
            onChange={(_, v) => v && setInterval(v)}
          >
            {INTERVALS.filter(i => allowedIntervals.includes(i.key)).map(i => (
              <ToggleButton key={i.key} value={i.key}>{i.label}</ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>

        {candles.length > 0 && (
          <Box sx={{ mb: 2, p: 2, bgcolor: isDark ? 'grey.800' : 'grey.50', borderRadius: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={6} sm={3}>
                <Typography variant="body2" color="textSecondary">Current Price</Typography>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>{formatCurrency(last)}</Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="body2" color="textSecondary">Gain/Loss</Typography>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: gainLoss >= 0 ? 'success.main' : 'error.main' }}>
                  {formatCurrency(gainLoss)}
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="body2" color="textSecondary">Percentage G/L</Typography>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: gainLossPct >= 0 ? 'success.main' : 'error.main' }}>
                  {gainLossPct.toFixed(2)}%
                </Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="body2" color="textSecondary">Price Range</Typography>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  {formatCurrency(Math.min(...closes))} - {formatCurrency(Math.max(...closes))}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
            <CircularProgress />
            <Typography variant="body2" sx={{ ml: 2 }}>Loading chart data...</Typography>
          </Box>
        ) : error ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
            <Typography variant="body2" color="error">{error}</Typography>
          </Box>
        ) : (
          <Box sx={{ height: 400 }}>
            <Box ref={containerRef} sx={{ height: '100%', width: '100%' }} />
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ChartPopup;
